import { useEffect, useRef, useState } from 'react';
import { UploadCloud, History } from 'lucide-react';
import Modal from '../common/Modal';
import FileTypeIcon from './FileTypeIcon';
import { useUploads } from '../../context/UploadContext';
import { getExtension, ACCEPT_ATTRIBUTE } from '../../utils/fileTypes';
import { formatBytes } from '../../utils/format';

/**
 * "Replace" = upload a new version. The previous version is kept and stays restorable;
 * nothing is overwritten (UX Rule 9). Goes through the same presign → PUT → commit path
 * as a new upload, with the document id attached.
 */
export default function ReplaceDialog({ open, onClose, document: doc }) {
  const { addFiles } = useUploads();
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setNote('');
      setError(null);
    }
  }, [open]);

  if (!doc) return null;

  const pick = (f) => {
    if (!f) return;
    setError(null);
    // The new file must be the same type: a PDF stays a PDF across versions.
    if (getExtension(f.name) !== getExtension(doc.name)) {
      setError(`The new version must be a .${getExtension(doc.name)} file, like the current one.`);
      return;
    }
    setFile(f);
  };

  const submit = () => {
    if (!file) return;
    const result = addFiles([file], doc.folderId, { documentId: doc.id, changeNote: note.trim() || 'Replaced file' });
    if (result.rejected.length) {
      setError(result.rejected[0].reason);
      return;
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} width={480}>
      <div className="stack" style={{ gap: 16, paddingTop: 22 }}>
        <div className="row" style={{ gap: 12 }}>
          <FileTypeIcon name={doc.name} size="lg" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16, overflowWrap: 'anywhere' }}>Upload new version</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              {doc.name} · currently v{doc.currentVersionNo}
            </div>
          </div>
        </div>

        <div className="callout row" style={{ gap: 8 }}>
          <History size={15} />
          <span>
            This becomes <strong>v{doc.currentVersionNo + 1}</strong>. Version {doc.currentVersionNo} is kept and can be restored at any time.
          </span>
        </div>

        <div
          className={`dropzone ${file ? '' : ''}`}
          style={{ padding: 24 }}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pick(e.dataTransfer.files?.[0]);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        >
          <div className="dropzone__icon">
            <UploadCloud size={20} />
          </div>
          {file ? (
            <div>
              <div style={{ fontWeight: 600 }}>{file.name}</div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                {formatBytes(file.size)} · click to choose a different file
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 600 }}>Drop the new file here</div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                or click to browse · must be a .{getExtension(doc.name)} file
              </div>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            className="sr-only"
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>

        <div>
          <label className="lbl" htmlFor="replace-note">
            What changed? (optional)
          </label>
          <input id="replace-note" className="in" placeholder="e.g. Revised pricing after client call" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
        </div>

        {error && <div className="banner banner--danger">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-g" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-p" onClick={submit} disabled={!file}>
            Upload as v{doc.currentVersionNo + 1}
          </button>
        </div>
      </div>
    </Modal>
  );
}
